import { createSlice } from "@reduxjs/toolkit";
import { DataRequest } from "src/utils/data";

const DATA_SELECT_GROUPS = {
  CC: "경제",
  DR1M: "경제",
  DR1D: "경제",
  ACI: "거래지수",
  RPHI: "거래지수",
  NHDI: "거래지수",
  IDI: "거래지수",
  RPC: "거래량",
  BYC: "거래량",
  BYS: "거래량",
  SLC: "거래량",
  SLS: "거래량",
  CNB: "거래량",
  BRC: "거래량",
  BRS: "거래량",
  BAC: "거래량",
  BAS: "거래량",
  MPISC: "매물시세",
  NHDC: "거래량",
  IDC: "거래량",
  PUVC: "저평가",
  PUVS: "저평가",
  DMPIQ: "5분위",
};

const HIDDEN_DATA_SELECT_NAMES = new Set([
  "MRPC", "BYC", "BYS", "SLC", "SLS", "CNB", "BRS", "BAS",
  "PPPMX", "PPPMN", "RFDMX", "RFDMN", "DLGMX", "DLGMN", "MPISC", "SS",
]);

const DATA_SELECT_MENU_ORDER = [
  "PI", "RPI", "PICR", "SC", "매물시세", "부동산원 시세", "UC", "MIC",
  "거래량", "거래지수", "저평가", "5분위", "RP", "ADI", "SUI", "PV",
  "PB", "경제", "PD", "HD",
];

/** Keep individual metrics and submenu groups in one ordered menu. */
export const getDataSelectMenuEntries = (options) => {
  const entries = [];
  const groups = new Map();
  for (const option of options) {
    if (!isDataSelectVisible(option.dataName)) continue;
    if (option.group === "기타") {
      entries.push({ key: option.value, orderKey: option.dataName, option });
    } else if (groups.has(option.group)) {
      groups.get(option.group).items.push(option);
    } else {
      const entry = { key: option.group, orderKey: option.group, label: option.group, items: [option] };
      groups.set(option.group, entry);
      entries.push(entry);
    }
  }
  const rank = (entry) => {
    const index = DATA_SELECT_MENU_ORDER.indexOf(entry.orderKey);
    return index < 0 ? DATA_SELECT_MENU_ORDER.length : index;
  };
  return entries.sort((a, b) => rank(a) - rank(b));
};

/** 분석 만들기 데이터 선택 메뉴에서 지표의 사용자 분류를 반환한다. */
export const getDataSelectGroup = (dataName) =>
  DATA_SELECT_GROUPS[dataName] ?? "기타";

/** Hide unlisted metrics from selection while retaining their data and saved charts. */
export const isDataSelectVisible = (dataName) =>
  !HIDDEN_DATA_SELECT_NAMES.has(dataName);

const DATA_SELECT_DESCRIPTIONS = {
  BRC: "매입자 거주지를 기준으로 외부지역·해당지역 거래를 구분합니다. 군구 선택은 같은 군구, 시도 선택은 같은 시도 거주자가 해당지역입니다.",
  BAC: "R-ONE 매입자 연령대별 아파트 매매 거래량입니다. 합계를 제외한 연령대와 기타를 표시합니다.",
  RCR: "한국부동산원 R-ONE이 공표한 아파트 지역별 전월세전환율(연 %)입니다. 전국·시도·시군구의 공표값을 제공합니다.",
  PI: "KB 매매·전세 가격지수의 변화를 보여줍니다.",
  PICR: "KB 가격지수의 기간별 증감률을 보여줍니다.",
  DLG: "매매가격과 전세가격의 차이를 보여줍니다.",
  DLGMX: "상위 시세 기준 매매가격과 전세가격의 차이를 보여줍니다.",
  DLGMN: "하위 시세 기준 매매가격과 전세가격의 차이를 보여줍니다.",
  RFD: "매매가격 대비 전세가격의 비율을 보여줍니다.",
  RFDMX: "상위 시세 기준 매매가격 대비 전세가격 비율을 보여줍니다.",
  RFDMN: "하위 시세 기준 매매가격 대비 전세가격 비율을 보여줍니다.",
  PPP: "가격을 공급면적 평수로 나눈 평당가를 보여줍니다.",
  PPPMX: "상위 가격 기준 평당가를 보여줍니다.",
  PPPMN: "하위 가격 기준 평당가를 보여줍니다.",
  AVP: "선택 범위의 평균 가격을 보여줍니다.",
  AVPPA: "면적 구간별 평균 가격을 보여줍니다.",
  PIPA: "면적 구간별 가격지수 흐름을 보여줍니다.",
  ADI: "매수 우위와 매도 우위의 정도를 지수로 보여줍니다.",
  ACI: "KB 조사 기준 매매·전세 거래의 활발한 정도를 지수로 보여줍니다.",
  SUI: "수요와 공급의 상대적인 강도를 지수로 보여줍니다.",
  PV: "중개업소가 전망한 향후 가격 방향을 지수로 보여줍니다.",
  MIC: "예정되거나 완료된 아파트 입주 세대수를 보여줍니다.",
  DR1M: "한국은행의 시도별 가계·주택관련·기업대출 연체율(%, 1개월 기준)입니다. 전국은 지역 평균이 아닌 공표값입니다.",
  DR1D: "전국 은행의 가계·기업대출 연체율(%, 1일 이상)입니다. 월별·연간 장기 자료이며 1개월 기준 통계와 구분합니다.",
  PB: "소득과 대출 조건을 반영한 주택 구입 부담 수준을 보여줍니다.",
  RP: "직거래를 제외한 국토교통부 실거래 가격을 보여줍니다.",
  RPC: "국토교통부 실거래 계약 건수를 보여줍니다.",
  RPHI: "거래량을 아파트 1,000세대당 건수로 보정한 거래지수입니다.",
  MRPC: "아파트 공급평형에 매핑된 국토교통부 실거래 건수를 보여줍니다.",
  NHDC: "과거 최고가격을 갱신한 실거래 건수를 보여줍니다.",
  NHDI: "신고가 거래를 활성 아파트 1,000세대당 건수로 보정한 지수입니다.",
  IDC: "직전 유효 거래보다 가격이 상승한 실거래 건수를 보여줍니다.",
  IDI: "상승 거래를 활성 아파트 1,000세대당 건수로 보정한 지수입니다.",
  MPISC: "매매 매물시세가 이전 기간보다 상승한 공급평형 수를 보여줍니다.",
  BGSC: "급매 조건에 해당하는 공급평형 수를 보여줍니다.",
  PUVC: "가격저평가 기준을 충족한 공급평형 수를 보여줍니다.",
  PUVS: "100을 중립으로 하며 값이 클수록 비교 대상보다 저평가된 정도가 큰 지수입니다.",
  SC: "네이버에 등록된 매매·전세·월세 매물 수를 보여줍니다.",
  UC: "준공 전·후 미분양 주택 수를 보여줍니다.",
  MPI: "한국부동산원 시세와 이를 집계한 지역·단지 가격 흐름을 보여줍니다.",
  MPIMX: "부동산원 시세의 상위 가격 흐름을 보여줍니다.",
  MPIMN: "부동산원 시세의 하위 가격 흐름을 보여줍니다.",
  MP: "네이버 매물에 표시된 시세 흐름을 보여줍니다.",
  MPMX: "네이버 매물의 상위 시세 흐름을 보여줍니다.",
  MPMN: "네이버 매물의 하위 시세 흐름을 보여줍니다.",
  DQP: "KB 매매가격을 1~5분위로 나눈 분위별 가격을 보여줍니다.",
  LQP: "KB 전세가격을 1~5분위로 나눈 분위별 가격을 보여줍니다.",
  DMPQ: "매매 매물시세를 1~5분위로 나눈 분위별 가격을 보여줍니다.",
  LMPQ: "전세 매물시세를 1~5분위로 나눈 분위별 가격을 보여줍니다.",
  SPC: "인허가·착공·준공·분양 기준 주택 공급 물량을 보여줍니다.",
  CC: "시중 통화량의 변화를 보여줍니다.",
  PD: "선택 지역의 인구수 변화를 보여줍니다.",
  HD: "선택 지역의 세대수 변화를 보여줍니다.",
  SS: "가격·거래·공급 지표를 종합한 상승진단 점수를 보여줍니다.",
};

/** 데이터 선택 메뉴의 도움말 문구를 반환한다. */
export const getDataSelectDescription = (dataName, label) =>
  DATA_SELECT_DESCRIPTIONS[dataName]
  ?? `${label}의 기간별 변화를 보여주는 데이터입니다.`;

class DataLakeTypes {
  types;
  constructor(types, dataRanges, legacyTypes = []) {
    this.types = types.map((t) => new DataLakeType(t));
    this.typesMap = new Map([...legacyTypes.map(t => new DataLakeType(t)), ...this.types].map(t => [t.dataName, t]));
    this.tabs = new Map();
    this.dateTypes = new Map();
    this.dataTypes = new Map();
    this.dataSources = new Map(); // dataName: DataSources

    this.dataRangesMap = new Map(); // dataRange: {value, label}
    this.dataRanges = dataRanges; // [S, G, D...]

    this.requests = this._buildRequests([...legacyTypes, ...types]);
  }

  getDataLakeTypeOption = (dataName) => {
    const type = this.typesMap.get(dataName);
    return type != null ? type.option : null;
  };

  _buildRequests = (types) => {
    const requests = new Map();
    for (var type of types) {
      for (var dt of type.dataTypes) {
        for (var p of type.dateTypes) {
          for (var r of type.dataRanges) {
            if (!this.dataRangesMap.has(r.value)) {
              this.dataRangesMap.set(r.value, r);
            }
            for (var s of type.dataSources) {
              const request = new DataRequest(
                dt.value,
                type.dataName,
                p.value,
                r.value,
                s.value,
                {},
                {},
                dt.displayName,
              );
              requests.set(request.getKey(), request);
            }
          }
        }
      }
    }
    return requests;
  };

  getAllDataRanges = () => {
    const ranges = [];
    for (const dataRange of this.dataRanges) {
      if (this.dataRangesMap.has(dataRange)) {
        ranges.push(this.dataRangesMap.get(dataRange));
      }
    }
    return ranges;
  };

  // 이건 {label, value} 둘다 존재
  getFullDataRangesByDataName = (dataName, enableFilter = false) => {
    const type = this.typesMap.get(dataName);
    if (enableFilter && type.hasFilter()) {
      return type != null ? type.option.dataRanges : [];
    } else {
      return type != null ? type.dataRanges : [];
    }
  };

  // 이건 values만 있는것
  getDataRangesByDataName = (dataName, enableFilter = false) => {
    const type = this.typesMap.get(dataName);
    if (enableFilter && type.hasFilter()) {
      return type != null ? type.option.dataRangesV : [];
    } else {
      return type != null ? type.dataRangesV : [];
    }
  };

  hasFilter = (dataName) => {
    const type = this.typesMap.get(dataName);
    return type != null ? type.hasFilter() : false;
  };

  includesRange = (dataName, dataRange, enableFilter = false) => {
    const type = this.typesMap.get(dataName);
    return type != null ? type.includesRange(dataRange, enableFilter) : false;
  };

  getChartPropsByNewDataName = (dataRange, dataName) => {
    // DataName 변경시 날짜와 타입도 변경 해줘야함.
    // DisplayName도 이때 업데이트 필요.
    const dataTypes = this.getDataTypeOptions(dataName);
    const dateTypes = this.getDateTypeOptions(dataName);
    const dataSources = this.getDataSourceOptions(dataName);

    // data name이 변경되면 Datatype과 period도 변경해줘야함
    const dataType = dataTypes.default;
    const dateType = dateTypes.default;
    const dataSource = dataSources.default;
    const props = {
      dataName,
      dataType,
      dateType,
      dataSource,
      // dataName 변경 시 멀티 선택 초기화
      dataTypes: [],
    };
    return props;
  };

  getChartPropsByNewDataRange = (dataRange) => {
    const tabs = this.getTabOptions(dataRange);
    const dataName = tabs.default;
    const props = this.getChartPropsByNewDataName(dataRange, dataName);
    return {
      ...props,
      dataRange,
    };
  };

  /**
   * @param {string} dataRange
   * @returns {Tabs}
   */
  getAllTabs = () => {
    const tabs = new Tabs(false, null);
    for (var type of this.types) {
      tabs.addOption({ label: type.displayName, value: type.dataName });
    }
    return tabs;
  };

  /**
   * @param {string} dataRange
   * @returns {Tabs}
   */
  getTabOptions = (dataRange) => {
    if (!this.tabs.has(dataRange)) {
      const tabs = new Tabs(false, null);
      for (var type of this.types) {
        if (type.includesRange(dataRange)) {
          tabs.addOption({ label: type.displayName, value: type.dataName });
        }
      }
      this.tabs.set(dataRange, tabs);
    }
    return this.tabs.get(dataRange);
  };

  /**
   * 분석 만들기 데이터 드롭다운 옵션을 만든다.
   * 같은 dataName이라도 KB/매물은 dateType과 dataSource가 달라서 선택 값을 3-tuple로 구분한다.
   */
  getDataSelectOptions = () => {
    const options = [];
    const added = new Set();

    const add = (group, label, dataName, dateType, dataSource, ranges = null) => {
      if (!isDataSelectVisible(dataName)) return;
      const type = this.typesMap.get(dataName);
      if (!type) return;
      if (!type.includesDateType(dateType) || !type.includesDataSource(dataSource)) return;
      const supportRanges = ranges
        ? type.dataRangesV.filter((range) => ranges.includes(range))
        : type.dataRangesV;
      if (supportRanges.length === 0) return;

      const value = this.getDataSelectValue(dataName, dateType, dataSource);
      if (added.has(value)) return;
      added.add(value);
      options.push({
        group,
        label,
        description: getDataSelectDescription(dataName, label),
        value,
        dataName,
        dateType,
        dataSource,
        supportRanges,
      });
    };

    const kabRanges = ["SP", "EP", "MP", "A", "D", "G", "S", "GR"];
    const kabPriceInfoRanges = ["SP", "EP", "MP", "A"];
    const marketRanges = ["P", "SP", "EP", "MP", "A", "D", "G", "S", "GR"];
    const marketPyeongRanges = ["P", "SP", "EP", "MP", "A"];

    add("부동산원 시세", "부동산원 시세", "MPI", "M", "KAB", kabRanges);
    add("부동산원 시세", "부동산원 상위시세", "MPIMX", "M", "KAB", kabPriceInfoRanges);
    add("부동산원 시세", "부동산원 하위시세", "MPIMN", "M", "KAB", kabPriceInfoRanges);
    add("부동산원 시세", "부동산원 전세가율", "RFD", "M", "KAB", kabRanges);
    add("부동산원 시세", "부동산원 평당가", "PPP", "M", "KAB", kabRanges);
    add("부동산원 시세", "부동산원 매전갭", "DLG", "M", "KAB", kabRanges);

    add("매물시세", "매물시세", "MP", "D", "ONN", marketRanges);
    add("매물시세", "상위매물시세", "MPMX", "D", "ONN", marketRanges);
    add("매물시세", "하위매물시세", "MPMN", "D", "ONN", marketRanges);
    add("매물시세", "매물전세가율", "RFD", "D", "ONN", marketRanges);
    add("매물시세", "매물평당가", "PPP", "D", "ONN", marketRanges);
    add("매물시세", "매물매전갭", "DLG", "D", "ONN", marketRanges);

    add("매물시세", "상위매물평당가", "PPPMX", "D", "ONN", marketPyeongRanges);
    add("매물시세", "하위매물평당가", "PPPMN", "D", "ONN", marketPyeongRanges);
    add("매물시세", "상위매물전세가율", "RFDMX", "D", "ONN", marketPyeongRanges);
    add("매물시세", "하위매물전세가율", "RFDMN", "D", "ONN", marketPyeongRanges);
    add("매물시세", "상위매물매전갭", "DLGMX", "D", "ONN", marketPyeongRanges);
    add("매물시세", "하위매물매전갭", "DLGMN", "D", "ONN", marketPyeongRanges);

    add("5분위", "KB 매매5분위", "DQP", "M", "KBS");
    add("5분위", "KB 전세5분위", "LQP", "M", "KBS");
    add("5분위", "부동산원 매매5분위", "DMPIQ", "M", "KAB");
    add("5분위", "매매매물시세5분위", "DMPQ", "M", "ONN");
    add("5분위", "전세매물시세5분위", "LMPQ", "M", "ONN");

    for (const type of this.types) {
      if (!isDataSelectVisible(type.dataName)) continue;
      const dateType = type.dateTypes[0]?.value;
      const dataSource = type.dataSources[0]?.value;
      if (!dateType || !dataSource) continue;

      const value = this.getDataSelectValue(type.dataName, dateType, dataSource);
      if (added.has(value)) continue;
      added.add(value);
      options.push({
        group: getDataSelectGroup(type.dataName),
        label: type.displayName,
        description: getDataSelectDescription(type.dataName, type.displayName),
        value,
        dataName: type.dataName,
        dateType,
        dataSource,
        supportRanges: type.dataRangesV,
      });
    }

    return options;
  };

  /** dataName/dateType/dataSource 조합을 드롭다운 값으로 직렬화한다. */
  getDataSelectValue = (dataName, dateType, dataSource) =>
    `${dataName}|${dateType}|${dataSource}`;

  /** 드롭다운 선택 값을 차트 업데이트용 props로 변환한다. */
  getChartPropsByDataSelectValue = (dataRange, value) => {
    const [dataName, dateType, dataSource] = value.split("|");
    const props = this.getChartPropsByNewDataName(dataRange, dataName);
    const option = this.getDataSelectOptions().find((o) => o.value === value);
    const nextDataRange = option?.supportRanges?.includes(dataRange)
      ? dataRange
      : option?.supportRanges?.[0] ?? dataRange;
    return {
      ...props,
      dataRange: nextDataRange,
      dataName,
      dateType,
      dataSource,
      displayName: option?.label ?? props.displayName,
    };
  };

  getDataTypeOptions = (dataName) => {
    if (!this.dataTypes.has(dataName)) {
      const type = this.typesMap.get(dataName);
      const dataTypes = new DataTypes(false, null, []);
      if (type) {
        for (var dataType of type.dataTypes) {
          dataTypes.addOption(dataType);
        }
        // 서버에서 multiSelectDataType 옵션이 있으면 멀티 선택 활성화
        if (type.option && type.option.multiSelectDataType) {
          dataTypes.isMultiSelect = true;
        }
      }
      this.dataTypes.set(dataName, dataTypes);
    }
    return this.dataTypes.get(dataName);
  };

  getDateTypeOptions = (dataName) => {
    if (!this.dateTypes.has(dataName)) {
      const type = this.typesMap.get(dataName);
      const dateType = new DateTypes(false, null, []);
      if (type) {
        for (var p of type.dateTypes) {
          dateType.addOption(p);
        }
      }
      this.dateTypes.set(dataName, dateType);
    }
    return this.dateTypes.get(dataName);
  };

  getDataSourceOptions = (dataName) => {
    if (!this.dataSources.has(dataName)) {
      const type = this.typesMap.get(dataName);
      const dataSource = new DataSources(false, null, []);
      if (type) {
        for (var p of type.dataSources) {
          dataSource.addOption(p);
        }
      }
      this.dataSources.set(dataName, dataSource);
    }
    return this.dataSources.get(dataName);
  };

  getRequest = (requestId) => {
    return this.requests.get(requestId);
  };
}

const initialState = {
  types: new DataLakeTypes([]),
  typesInit: false,
};

class DateTypes {
  enable;
  default;
  options;
  isMulti;
  constructor(enable, def, options = []) {
    this.enable = enable;
    this.default = def;
    this.options = options;
  }

  includes = (dateType) => {
    for (var option of this.options) {
      if (option.value === dateType) {
        return true;
      }
    }
    return false;
  };

  addOption = (option) => {
    if (this.default === null) {
      this.default = option.value;
    }
    this.options.push(option);
    this.enable = true;
    if (this.options.length > 1) {
      this.isMulti = true;
    }
  };
}

class DataTypes {
  enable;
  default;
  options;
  isMulti;
  constructor(enable, def, options) {
    this.enable = enable;
    this.default = def;
    this.options = options;
  }

  includes = (dataType) => {
    for (var option of this.options) {
      if (option.value === dataType) {
        return true;
      }
    }
    return false;
  };

  includesList = (dataTypes) => {
    if (!dataTypes) {
      return false;
    }
    const values = this.options.map((o) => o.value);
    for (const dataType of dataTypes) {
      if (!values.includes(dataType)) {
        return false;
      }
    }
    return true;
  };

  addOption = (option) => {
    if (this.default === null) {
      this.default = option.value;
    }
    this.options.push(option);
    this.enable = true;
    if (this.options.length > 1) {
      this.isMulti = true;
    }
  };
}

class DataSources {
  enable;
  default;
  options;
  isMulti;
  constructor(enable, def, options) {
    this.enable = enable;
    this.default = def;
    this.options = options;
  }

  includes = (dataType) => {
    for (var option of this.options) {
      if (option.value === dataType) {
        return true;
      }
    }
    return false;
  };

  includesList = (dataTypes) => {
    if (!dataTypes) {
      return false;
    }
    const values = this.options.map((o) => o.value);
    for (const dataType of dataTypes) {
      if (!values.includes(dataType)) {
        return false;
      }
    }
    return true;
  };

  addOption = (option) => {
    if (this.default === null) {
      this.default = option.value;
    }
    this.options.push(option);
    this.enable = true;
    if (this.options.length > 1) {
      this.isMulti = true;
    }
  };
}

class Tabs {
  default;
  options;
  length;
  constructor(enable, def, options = []) {
    this.enable = enable;
    this.default = def;
    this.options = options;
    this.length = options.length;
    this.map = new Map();
  }

  addOption = (option) => {
    if (this.default === null) {
      this.default = option.value;
    }
    this.options.push(option);
    this.length = this.options.length;
    this.map.set(option.value, option);
  };

  has = (dataName) => {
    return this.map.has(dataName);
  };
}

class DataLakeType {
  dataName;
  dataTypes;
  displayName;
  dateTypes;
  dataRanges;
  dataSources;
  constructor(type) {
    // s 붙은건 value, label 이고 datanName, dataSource는 그냥 value로 사용
    this.dataName = type.dataName.value;
    this.dataNameLabel = type.dataName.label;
    this.dataTypes = type.dataTypes;
    this.dateTypes = type.dateTypes;
    this.dateTypes = type.dateTypes;
    this.dataRanges = type.dataRanges;
    this.dataRangesV = type.dataRanges.map((o) => o.value);
    this.dataSources = type.dataSources;
    this.displayName = type.displayName;
    if (type.option) {
      this.option = new DataLakeOption(type.option);
    }
  }

  includesRange = (dataRange, enableFilter = false) => {
    // hasFilter이면 this.option은 무조건 존재
    if (enableFilter && this.hasFilter()) {
      // 필터는 별도의 range를 가짐
      return this.option.includesRange(dataRange);
    } else {
      return this.dataRangesV.includes(dataRange);
    }
  };

  /** 지정 날짜 타입을 지원하는지 확인한다. */
  includesDateType = (dateType) => {
    return this.dateTypes.some((option) => option.value === dateType);
  };

  /** 지정 데이터 소스를 지원하는지 확인한다. */
  includesDataSource = (dataSource) => {
    return this.dataSources.some((option) => option.value === dataSource);
  };

  hasFilter() {
    return this.option && this.option.filter;
  }
}

class DataLakeOption {
  constructor(option) {
    Object.entries(option).forEach(([key, value]) => {
      this[key] = value; // 각 키와 값을 this (클래스 인스턴스)에 동적으로 할당합니다.
    });
    this.filter = option.filter;
    this.dataRanges = option.filter ? option.filter.dataRanges : [];
    this.dataRangesV = option.filter
      ? option.filter.dataRanges.map((o) => o.value)
      : [];
  }

  includesRange = (dataRange) => {
    return this.dataRangesV.includes(dataRange);
  };
}

const reducers = {
  getTypes(state, action) {
    const types = action.payload;
    state.types = new DataLakeTypes(types.types, types.dataRanges, types.legacyTypes);
    state.typesInit = true;
  },
};

export const slice = createSlice({
  name: "datatype",
  initialState,
  reducers,
});

export const { reducer } = slice;
